
import { useState, useEffect, useRef, useCallback } from 'react';
import { UseFormReturn } from "react-hook-form";
import { isValid } from 'date-fns';
import { formatDateForInput, parseDateFromInput, isValidDateFormat } from '../utils/dateUtils';
import { DateChangeHandler, isNoOpDateChangeHandler, safeFormatDate } from '../utils/datePickerUtils';

interface UseDateRangePickerStateProps {
  form: UseFormReturn<any>;
  startDateName: string;
  endDateName: string;
  onDateChange?: DateChangeHandler;
}

export const useDateRangePickerState = ({
  form,
  startDateName,
  endDateName,
  onDateChange = () => {}
}: UseDateRangePickerStateProps) => {
  // Get start and end dates from form
  const startDate = form.watch(startDateName);
  const endDate = form.watch(endDateName);
  
  // Local state for input values
  const [startInputValue, setStartInputValue] = useState('');
  const [endInputValue, setEndInputValue] = useState('');
  
  // Track selection mode: 'start' or 'end'
  const [selectionMode, setSelectionMode] = useState<'start' | 'end'>('start');
  
  // Track update state
  const updateLockRef = useRef(false);
  const lastUpdateTimeRef = useRef(0);
  
  // Initialize input values from form dates
  useEffect(() => {
    setStartInputValue(safeFormatDate(startDate));
    setEndInputValue(safeFormatDate(endDate));
  }, [startDate, endDate]);

  // Safe trigger for onDateChange with protection against loops
  const triggerDateChange = useCallback((fieldName: string) => {
    // Skip if locked or the handler is a no-op
    if (updateLockRef.current || isNoOpDateChangeHandler(onDateChange)) {
      return;
    }
    
    // Prevent rapid consecutive calls (throttling)
    const now = Date.now();
    if (now - lastUpdateTimeRef.current < 50) {
      return;
    }
    
    // Lock updates, call handler, then unlock after a delay
    updateLockRef.current = true;
    lastUpdateTimeRef.current = now;
    
    onDateChange(fieldName);
    
    // Release lock after a short delay
    setTimeout(() => {
      updateLockRef.current = false;
    }, 50);
  }, [onDateChange]);

  // Handle date click from calendar
  const handleDateClick = useCallback((date: Date) => {
    if (selectionMode === 'start') {
      // First click - set start date
      form.setValue(startDateName, date, { shouldValidate: true });
      setSelectionMode('end');
      triggerDateChange(startDateName);
      
      // If end date exists and is before the new start date, clear it
      if (endDate && date > endDate) {
        form.setValue(endDateName, null, { shouldValidate: true });
        setEndInputValue('');
      }
    } else {
      // Second click - set end date
      // If selected end date is before start date, swap them
      if (startDate && date < startDate) {
        form.setValue(endDateName, startDate, { shouldValidate: true });
        form.setValue(startDateName, date, { shouldValidate: true });
        triggerDateChange(startDateName);
        triggerDateChange(endDateName);
      } else {
        form.setValue(endDateName, date, { shouldValidate: true });
        triggerDateChange(endDateName);
      }
      setSelectionMode('start'); // Reset to start selection for next time
    }
  }, [form, startDateName, endDateName, startDate, endDate, selectionMode, triggerDateChange]);

  // Handle start date input change
  const handleStartDateInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStartInputValue(value);
  }, []);

  // Handle end date input change
  const handleEndDateInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEndInputValue(value);
  }, []);

  // Handle start date input blur - parse and validate date
  const handleStartDateBlur = useCallback(() => {
    if (isValidDateFormat(startInputValue)) {
      const parsedDate = parseDateFromInput(startInputValue);
      if (parsedDate) {
        form.setValue(startDateName, parsedDate, { shouldValidate: true });
        triggerDateChange(startDateName);
      }
    } else {
      // Reset to the current valid date in form
      setStartInputValue(safeFormatDate(startDate));
    }
  }, [form, startDateName, startInputValue, startDate, triggerDateChange]);

  // Handle end date input blur - parse and validate date
  const handleEndDateBlur = useCallback(() => {
    if (isValidDateFormat(endInputValue)) {
      const parsedDate = parseDateFromInput(endInputValue);
      if (parsedDate) {
        form.setValue(endDateName, parsedDate, { shouldValidate: true });
        triggerDateChange(endDateName);
      }
    } else {
      // Reset to the current valid date in form
      setEndInputValue(safeFormatDate(endDate));
    }
  }, [form, endDateName, endInputValue, endDate, triggerDateChange]);

  return {
    // Date values
    startDate,
    endDate,
    startInputValue,
    endInputValue,
    
    // Event handlers
    handleDateClick,
    handleStartDateInputChange,
    handleEndDateInputChange,
    handleStartDateBlur,
    handleEndDateBlur
  };
};
